"use client";

import { useGenerateResumeMutation } from "@/features/profile";
import { areThoughtsReflected } from "@/features/thoughts";
import { useAuth, useUserChoices, useUserProfile } from "@/state/globalStore";
import { useCallback, useMemo, useState } from "react";
import { showErrorToast, showWarningToast } from "@/shared/feedback";
import type { ThoughtGrouping } from "@/generated/fastapi";
import { buildSelectedProfile } from "@/shared/utils/selectedProfile";

export default function useStudioProfileExport() {
  const { accessToken } = useAuth();
  const { userProfile } = useUserProfile();
  const {
    selectedSituations,
    countSelectedSituations,
    selectedThoughts,
    selectedThoughtsFree,
    isSelectedProfile,
  } = useUserChoices();
  const generateResumeMutation = useGenerateResumeMutation();
  const { mutateAsync, isPending, error } = generateResumeMutation;
  const [moveOnReflectionState, setMoveOnReflectionState] = useState<{
    status: "idle" | "checking" | "ready" | "blocked";
    selectionKey: string;
  }>({ status: "idle", selectionKey: "" });

  const linkMoveOnEnabled =
    userProfile.data.print_settings.link_moveon ?? false;
  const moveOnSelectionKey = useMemo(
    () =>
      JSON.stringify({
        selectedSituations,
        selectedThoughts,
        selectedThoughtsFree,
      }),
    [selectedSituations, selectedThoughts, selectedThoughtsFree],
  );
  const effectiveMoveOnReflectionStatus =
    !linkMoveOnEnabled ||
    !isSelectedProfile ||
    moveOnReflectionState.selectionKey !== moveOnSelectionKey
      ? "idle"
      : moveOnReflectionState.status;

  const checkMoveOnReflections = useCallback(async () => {
    if (!linkMoveOnEnabled || !isSelectedProfile) {
      setMoveOnReflectionState({
        status: "idle",
        selectionKey: moveOnSelectionKey,
      });
      return true;
    }

    setMoveOnReflectionState({
      status: "checking",
      selectionKey: moveOnSelectionKey,
    });

    try {
      const selectedProfile = buildSelectedProfile({
        selectedSituations,
        selectedThoughts,
        selectedThoughtsFree,
        includeFreeListOrder: true,
      });

      const allReflected = await areThoughtsReflected(
        accessToken,
        selectedProfile,
      );
      setMoveOnReflectionState({
        status: allReflected ? "ready" : "blocked",
        selectionKey: moveOnSelectionKey,
      });
      return allReflected;
    } catch {
      setMoveOnReflectionState({
        status: "blocked",
        selectionKey: moveOnSelectionKey,
      });
      return false;
    }
  }, [
    accessToken,
    isSelectedProfile,
    linkMoveOnEnabled,
    moveOnSelectionKey,
    selectedSituations,
    selectedThoughts,
    selectedThoughtsFree,
  ]);

  const moveOnReflectionBlockMessage =
    "Complete reflection for all selected thoughts before exporting with MoveOn linking enabled.";

  const isDownloadBlockedByMoveOn =
    linkMoveOnEnabled &&
    isSelectedProfile &&
    effectiveMoveOnReflectionStatus === "blocked";

  const handleDownloadResumeClick = useCallback(
    async (groupingType: ThoughtGrouping) => {
      // Prevent multiple clicks
      if (isPending) return;

      if (linkMoveOnEnabled) {
        const allReflected = await checkMoveOnReflections();
        if (!allReflected) {
          showWarningToast(moveOnReflectionBlockMessage);
          return;
        }
      }

      if (groupingType === "by_situation") {
        // Ensure at least one situation is selected
        if (countSelectedSituations() === 0) {
          showWarningToast("Please select at least one situation.");
          return;
        }

        // Ensure that selected thoughts are anchored to selected situations.
        const validSituationIds = new Set(
          selectedSituations.flatMap((s) => s.situations_ids),
        );
        for (const thought of selectedThoughts) {
          if (
            !thought.situation_id ||
            !validSituationIds.has(thought.situation_id)
          ) {
            showWarningToast(
              "All selected thoughts must be anchored to the selected situations. Select situations accordingly.",
            );
            return;
          }
        }
      } else if (
        selectedThoughtsFree.every(
          (selection) => selection.thoughts_ids.length === 0,
        )
      ) {
        showWarningToast("Please select at least one thought for Free List.");
        return;
      }

      // Proceed to generate the resume
      try {
        const selectedProfile = buildSelectedProfile({
          selectedSituations,
          selectedThoughts,
          selectedThoughtsFree,
          includeFreeListOrder: groupingType === "free_list",
        });

        const blob = await mutateAsync({
          groupingType,
          selectedProfile,
          userRequestedLinkMoveOn:
            userProfile.data.print_settings.link_moveon ?? false, // if not defined (also because lazy loaded) >>> assume false
        });

        const today = new Date();
        const dateStr = today.toISOString().split("T")[0];
        const filename = `resume_${dateStr}.docx`;
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(anchor);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "An error occurred while generating the CV.";
        showErrorToast(message);
      }
    },
    [
      countSelectedSituations,
      checkMoveOnReflections,
      isPending,
      linkMoveOnEnabled,
      mutateAsync,
      moveOnReflectionBlockMessage,
      selectedSituations,
      selectedThoughts,
      selectedThoughtsFree,
      userProfile.data.print_settings,
    ],
  );

  return useMemo(
    () => ({
      isGenerating: isPending,
      isCheckingMoveOnReflections:
        effectiveMoveOnReflectionStatus === "checking",
      isDownloadBlockedByMoveOn,
      moveOnReflectionBlockMessage,
      generatingError: error instanceof Error ? error.message : null,
      handleDownloadResumeClick,
      refreshMoveOnReflectionStatus: checkMoveOnReflections,
    }),
    [
      checkMoveOnReflections,
      error,
      handleDownloadResumeClick,
      effectiveMoveOnReflectionStatus,
      isDownloadBlockedByMoveOn,
      isPending,
      moveOnReflectionBlockMessage,
    ],
  );
}
